import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ApexCharts from 'apexcharts';
import { getOptionsChart } from '../../../utils/helpers/getOptionsChart';
import {
  sumArrays,
  costCalculator as calculator,
  toNumberOrZero,
} from '../../../utils/helpers';
import {
  CURRENT_TEMPLATE_NAME,
  COLORS,
  COLOURFUL_TEMPLATE_NAME,
  BLOG_FRIENDLY_TEMPLATE_NAME,
  ARCHITECTURAL_MINIMAL_TEMPLATE_NAME,
  TECH_MODERN_TEMPLATE_NAME,
  BOLD_TEMPLATE_NAME,
} from '../../../utils/const';

const { secondGrey, black } = COLORS;

const makeCumulative = (arr) => {
  const result = arr.map((o, i) => {
    if (i - 1 >= 0) {
      const thisValue =
        toNumberOrZero(o) +
        arr
          .slice(0, i)
          .reduce((sum, x) => toNumberOrZero(sum) + toNumberOrZero(x));

      return Math.floor(toNumberOrZero(thisValue));
    }
    return Math.floor(toNumberOrZero(o));
  });

  return result;
};

const Chart = ({
  locales,
  locales: { finance },
  firstChartColor,
  secondChartColor,
  borderCardColor,
  titleColor,
  textColor,
  templateName = CURRENT_TEMPLATE_NAME,
}) => {
  const chartRef = useRef(null);

  const [totalCashIn, setTotalCashIn] = useState([]);
  const [totalCashOut, setTotalCashOut] = useState([]);

  const chartStyles = {
    [CURRENT_TEMPLATE_NAME]: {
      fontFamily: 'Nunito',
      borderColor: secondGrey,
      colors: [firstChartColor, secondChartColor],
      themeStyle: 'light',
    },
    [COLOURFUL_TEMPLATE_NAME]: {
      fontFamily: 'Open Sans',
      borderColor: secondGrey,
      colors: [firstChartColor, secondChartColor],
      themeStyle: 'light',
    },
    [BLOG_FRIENDLY_TEMPLATE_NAME]: {
      fontFamily: 'Nunito',
      borderColor: secondChartColor,
      colors: [firstChartColor, black],
      themeStyle: 'light',
    },
    [ARCHITECTURAL_MINIMAL_TEMPLATE_NAME]: {
      fontFamily: 'Lato',
      borderColor: secondChartColor,
      colors: [firstChartColor, secondChartColor],
      themeStyle: 'light',
    },
    [TECH_MODERN_TEMPLATE_NAME]: {
      fontFamily: 'Nunito',
      borderColor: secondChartColor,
      colors: [firstChartColor, secondChartColor],
      themeStyle: 'light',
    },
    [BOLD_TEMPLATE_NAME]: {
      fontFamily: 'Martel Sans',
      borderColor: secondChartColor,
      colors: [firstChartColor, secondChartColor],
      textChartColor: textColor,
      themeStyle: 'dark',
    },
  };

  const { collection: products } = useSelector((state) => state.product);
  const { collection: cost } = useSelector((state) => state.cost);

  useEffect(() => {
    const apexChart = new ApexCharts(
      chartRef.current,
      getOptionsChart(
        locales,
        totalCashIn || [],
        totalCashOut || [],
        chartStyles,
        templateName,
      ),
    );
    apexChart.render().then(() => {});
    return () => {
      apexChart.destroy();
    };
  }, [totalCashOut, totalCashIn]);

  useEffect(() => {
    const newCashIn = products.map((product) => {
      const endUserPrice = +product.endUserPrice || 0;
      const months = product.sales.map((s) =>
        toNumberOrZero(toNumberOrZero(+s * endUserPrice).toFixed(2)),
      );

      return {
        name: product.name,
        months,
      };
    });

    const productArrays = newCashIn.map((i) => i.months);
    const newCashInTotal = sumArrays(...productArrays);

    const newFixed = cost.map((c) => {
      const months = new Array(12).fill(
        toNumberOrZero(toNumberOrZero(calculator(c)).toFixed(2)),
      );
      return {
        name: c.name,
        months,
      };
    });

    const newVariable = products.map((p) => {
      const months = p.sales.map((i) =>
        toNumberOrZero(toNumberOrZero(+p.costPrice * i).toFixed(2)),
      );
      return {
        name: p.name,
        months,
      };
    });

    const newCashOutTotal = sumArrays(
      ...[
        ...newFixed.map((i) => i.months),
        ...newVariable.map((i) => i.months),
      ],
    );

    setTotalCashOut(makeCumulative(newCashOutTotal));
    setTotalCashIn(makeCumulative(newCashInTotal));
  }, [products, cost]);

  return (
    <div className="chart custom-font-body">
      <div
        className="main-title custom-font-title"
        style={{ color: titleColor }}
      >
        {finance.chart.title}
      </div>
      <div className="chart-inner">
        <div
          className="chart-wrapper custom-font-body"
          style={{
            borderColor: borderCardColor,
          }}
          ref={chartRef}
        />
      </div>
    </div>
  );
};

export default Chart;
